import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, Zap, ShieldCheck, ShieldAlert } from 'lucide-react';
import { salesApi, serverError } from '../../lib/salesApi';

const CY = '#90E0EF';
const inputCls = 'w-full bg-[#0d0d0d] border border-[#222] text-[#f0f0f0] text-sm font-mono px-3 py-2.5 focus:outline-none focus:border-[#90E0EF] placeholder:text-[#555]';

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-[0.2em] text-[#777] mb-2">{label}</label>
      {children}
      {hint && <p className="text-[10px] font-mono text-[#555] mt-1.5">{hint}</p>}
    </div>
  );
}

export default function SalesSettingsPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tablesMissing, setTablesMissing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [secret, setSecret] = useState('');
  const [pub, setPub] = useState('');
  const [webhook, setWebhook] = useState('');

  const webhookUrl = `${window.location.origin}/api/webhooks/stripe`;

  function load() {
    setLoading(true);
    salesApi.stripeSettings()
      .then((d) => { setStatus(d); setTablesMissing(false); })
      .catch((e) => { if (e?.response?.status === 503) setTablesMissing(true); else toast.error(serverError(e)); })
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try {
      const body = {};
      if (secret.trim()) body.stripe_secret_key = secret.trim();
      if (pub.trim()) body.stripe_publishable_key = pub.trim();
      if (webhook.trim()) body.stripe_webhook_secret = webhook.trim();
      if (Object.keys(body).length === 0) { toast.message('Nothing to save — enter a key to update.'); setSaving(false); return; }
      const d = await salesApi.saveStripe(body);
      setStatus(d); setSecret(''); setPub(''); setWebhook('');
      toast.success('Stripe connection saved');
    } catch (e) {
      if (e?.response?.status === 503) setTablesMissing(true);
      toast.error(serverError(e));
    } finally { setSaving(false); }
  }

  async function test() {
    setTesting(true); setTestResult(null);
    try {
      const r = await salesApi.testStripe();
      setTestResult({ ok: true, ...r });
      toast.success('Connected to Stripe');
    } catch (e) {
      setTestResult({ ok: false, error: serverError(e) });
      toast.error(serverError(e, 'Stripe connection failed'));
    } finally { setTesting(false); }
  }

  if (tablesMissing) {
    return <div className="border border-[#2a2416] bg-[#141207] px-6 py-8 text-center">
      <p className="font-outfit text-lg text-[#e4c07a]">Sales tables not initialised</p>
      <p className="text-xs font-mono text-[#aa9] mt-2">Stripe keys are read from the server environment until the SQL migration runs.</p>
    </div>;
  }

  const connected = status?.configured;

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-2.5 mb-1">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: CY, boxShadow: `0 0 8px ${CY}` }} />
        <h2 className="font-outfit text-lg font-semibold text-white tracking-tight">Stripe // Connection</h2>
      </div>
      <p className="text-[11px] font-mono text-[#777] mb-6">Payments for Mr. Relative and The Relative Gallery run through one Stripe account. Keys are backend-only.</p>

      {loading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: CY }} /></div>
      ) : (
        <>
          {/* status line */}
          <div className="flex items-center justify-between border border-[#1a1a1a] bg-[#080808] px-5 py-4 mb-6" data-testid="stripe-status">
            <div className="flex items-center gap-3">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: connected ? '#7ed4a0' : '#555', boxShadow: connected ? '0 0 10px rgba(126,212,160,0.6)' : 'none' }} />
              <span className="text-xs font-mono uppercase tracking-[0.16em]" style={{ color: connected ? '#7ed4a0' : '#888' }}>
                {connected ? 'Connected' : 'Not Configured'}
              </span>
            </div>
            <div className="text-right text-[10px] font-mono text-[#666]">
              {status?.mode && <span className="uppercase tracking-[0.14em] mr-3" style={{ color: status.mode === 'live' ? '#e4c07a' : '#7aa8d4' }}>{status.mode} mode</span>}
              <span>source: {status?.source}</span>
            </div>
          </div>

          {/* current masked values */}
          <div className="space-y-5 mb-6">
            <Field label="Stripe Secret Key">
              <input className={inputCls} placeholder={status?.secret_key_masked || 'sk_live_… (enter to set)'} value={secret} onChange={(e) => setSecret(e.target.value)} type="password" data-testid="stripe-secret-input" />
            </Field>
            <Field label="Stripe Publishable Key" hint={status?.publishable_key_preview ? `Current: ${status.publishable_key_preview}` : undefined}>
              <input className={inputCls} placeholder={status?.publishable_key_masked || 'pk_live_…'} value={pub} onChange={(e) => setPub(e.target.value)} data-testid="stripe-pub-input" />
            </Field>
            <Field label="Stripe Webhook Secret" hint={status?.webhook_secret_set ? 'A webhook secret is set.' : 'Not set yet — add it after registering the webhook in Stripe.'}>
              <input className={inputCls} placeholder="whsec_…" value={webhook} onChange={(e) => setWebhook(e.target.value)} type="password" data-testid="stripe-webhook-input" />
            </Field>
          </div>

          <div className="flex flex-wrap gap-3 mb-8">
            <button onClick={save} disabled={saving} style={{ cursor: 'pointer', background: CY, color: '#050505' }}
              data-testid="stripe-save-btn" className="inline-flex items-center gap-2 px-5 py-2.5 text-[11px] font-mono uppercase tracking-[0.18em] disabled:opacity-40">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Connection
            </button>
            <button onClick={test} disabled={testing} style={{ cursor: 'pointer' }}
              data-testid="stripe-test-btn" className="inline-flex items-center gap-2 px-5 py-2.5 text-[11px] font-mono uppercase tracking-[0.18em] border border-[#2a2a2a] text-[#ccc] hover:border-[#90E0EF]/50 hover:text-white disabled:opacity-40">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Test Connection
            </button>
          </div>

          {testResult && (
            <div className="border px-5 py-4 mb-8" data-testid="stripe-test-result"
              style={{ borderColor: testResult.ok ? 'rgba(126,212,160,0.4)' : 'rgba(232,139,160,0.4)', background: testResult.ok ? 'rgba(126,212,160,0.06)' : 'rgba(232,139,160,0.06)' }}>
              {testResult.ok ? (
                <>
                  <p className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em]" style={{ color: '#7ed4a0' }}><ShieldCheck className="w-4 h-4" /> Connected to Stripe</p>
                  <div className="mt-3 grid grid-cols-2 gap-y-1.5 text-xs font-mono text-[#ccc]">
                    <span className="text-[#666]">Account</span><span>{testResult.account_id}</span>
                    <span className="text-[#666]">Country</span><span>{testResult.country}</span>
                    <span className="text-[#666]">Default currency</span><span>{testResult.default_currency}</span>
                    {testResult.business_name && (<><span className="text-[#666]">Business</span><span>{testResult.business_name}</span></>)}
                  </div>
                </>
              ) : (
                <p className="flex items-center gap-2 text-xs font-mono" style={{ color: '#e88ba0' }}><ShieldAlert className="w-4 h-4" /> {testResult.error}</p>
              )}
            </div>
          )}

          {/* webhook helper */}
          <div className="border border-[#1a1a1a] bg-[#080808] px-5 py-4">
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#666] mb-2">Webhook endpoint</p>
            <div className="flex items-center gap-2">
              <code className="text-xs font-mono text-[#90E0EF] break-all">{webhookUrl}</code>
              <button onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success('Copied'); }} style={{ cursor: 'pointer' }} className="text-[10px] font-mono text-[#777] hover:text-white shrink-0">copy</button>
            </div>
            <p className="text-[10px] font-mono text-[#555] mt-2">Register this in Stripe → Developers → Webhooks. Enable events: <span className="text-[#aaa]">checkout.session.completed</span>, <span className="text-[#aaa]">checkout.session.expired</span>. Then paste the signing secret above.</p>
          </div>
        </>
      )}
    </div>
  );
}
